/**
 * DecisionQueueView · Chunk 10 list surface.
 *
 * Tabular browse of decision_queue rows from Chunk 9. Filter by compute_method,
 * sort by gap_pct, click a row to open the five-panel DecisionModal.
 */
import React, { useEffect, useState, useMemo } from "react";
import { api, fmtUSD } from "../lib/api";
import DecisionModal from "./DecisionModal";

const TIFFANY = "#0D9488";
const SLATE_INK = "#0F172A";
const ALABASTER = "#FAF9F6";

const METHOD_PILL = {
  build_up:       { label: "Build-up",      bg: `${TIFFANY}22`, fg: TIFFANY },
  benchmark_only: { label: "Benchmark only", bg: "#FEF3C7",     fg: "#92400E" },
  no_match:       { label: "No match",       bg: "#FEE2E2",     fg: "#991B1B" },
  hybrid:         { label: "Hybrid",          bg: "#F1F5F9",     fg: "#334155" },
};

const ACTION_PILL = {
  switch:      { label: "switch",      bg: "#B45309",    fg: "#FFFFFF" },
  negotiate:   { label: "negotiate",   bg: TIFFANY,      fg: "#FFFFFF" },
  consolidate: { label: "consolidate", bg: "#0F172A",    fg: "#F9FAFB" },
  defer:       { label: "defer",       bg: "#F1F5F9",    fg: "#334155" },
  no_action:   { label: "no action",   bg: "#E5E7EB",    fg: "#374151" },
};

export default function DecisionQueueView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [audit, setAudit] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = methodFilter === "ALL" ? {} : { compute_method: methodFilter.toLowerCase() };
    Promise.all([
      api.decisionQueue({ ...params, limit: 200 }),
      api.evidenceAudit(),
    ])
      .then(([r, a]) => { setRows(r || []); setAudit(a); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [methodFilter]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ag = a.gap_pct ?? -9999;
      const bg = b.gap_pct ?? -9999;
      return Math.abs(bg) - Math.abs(ag);
    });
  }, [rows]);

  const buildUpCount  = rows.filter((r) => r.compute_method === "build_up").length;
  const totalGap = rows.reduce((s, r) => s + (r.gap_total_usd && r.gap_total_usd > 0 ? r.gap_total_usd : 0), 0);

  return (
    <div data-testid="decision-queue-view" style={{ color: SLATE_INK }}>
      {/* Header strip */}
      <div className="mb-5 grid md:grid-cols-4 gap-3">
        <Kpi label="Decisions in queue" value={String(rows.length)} />
        <Kpi label="Build-up coverage" value={`${buildUpCount} / ${rows.length}`} sub={`${rows.length ? Math.round((buildUpCount / rows.length) * 100) : 0}% match ≥0.7`} />
        <Kpi label="Total addressable gap" value={fmtUSD(totalGap)} tone="tiffany" />
        <Kpi
          label="Evidence traceable"
          value={audit?.pass ? "PASS" : audit ? "FAIL" : "—"}
          tone={audit?.pass ? "tiffany" : "amber"}
          sub={audit ? `${audit.total - audit.failing} of ${audit.total} build-ups verified` : null}
          testId="audit-pill"
        />
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {["ALL", "build_up", "benchmark_only", "no_match"].map((m) => (
          <button
            key={m}
            onClick={() => setMethodFilter(m)}
            data-testid={`filter-${m}`}
            className="text-xs px-3 py-1.5 rounded-full transition"
            style={{
              background: methodFilter === m ? TIFFANY : "#F1F5F9",
              color: methodFilter === m ? "#FFFFFF" : SLATE_INK,
            }}
          >
            {m === "ALL" ? "All" : METHOD_PILL[m]?.label || m}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-md overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] tracking-[0.18em] uppercase" style={{ background: "#F8FAFC", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>
          <div className="col-span-3">PO Line · Supplier</div>
          <div className="col-span-2">Template</div>
          <div className="col-span-2 text-right">Quoted / Supportable</div>
          <div className="col-span-1 text-right">Gap %</div>
          <div className="col-span-2 text-right">Gap $</div>
          <div className="col-span-2">Method · Action</div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: "#64748B" }}>Loading decisions…</div>
        ) : sorted.length === 0 ? (
          <div className="py-10 text-center text-sm" style={{ color: "#64748B" }}>
            No decisions in queue yet. Compute one via <code>POST /api/should-cost/compute</code> or open the artifact generator.
          </div>
        ) : (
          sorted.map((r) => (
            <div
              key={r.decision_id}
              data-testid={`decision-row-${r.decision_id}`}
              onClick={() => { setSelected(r.decision_id); setModalOpen(true); }}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 cursor-pointer transition border-b last:border-0"
              style={{ borderColor: "#F1F5F9" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <div className="col-span-3 text-xs">
                <div className="font-medium" style={{ color: SLATE_INK }}>{r.po_line_id || "—"}</div>
                <div className="text-[10px]" style={{ color: "#64748B" }}>{r.supplier_id || "—"}</div>
              </div>
              <div className="col-span-2 text-xs" style={{ color: "#475569" }}>
                {r.template_id ? (
                  <>
                    <div>{r.template_id}</div>
                    <div className="text-[10px]">match {(r.match_confidence * 100).toFixed(0)}%</div>
                  </>
                ) : (
                  <span className="italic">no template</span>
                )}
              </div>
              <div className="col-span-2 text-xs text-right tabular-nums" style={{ color: "#475569" }}>
                <div>{r.actual_price_per_unit == null ? "—" : `$${r.actual_price_per_unit.toFixed(2)}`}</div>
                <div className="text-[10px]" style={{ color: TIFFANY }}>
                  {r.supportable_price_per_unit == null ? "—" : `$${r.supportable_price_per_unit.toFixed(2)}`}
                </div>
              </div>
              <div className="col-span-1 text-xs text-right tabular-nums" style={{ color: gapColor(r.gap_pct) }}>
                {r.gap_pct == null ? "—" : `${r.gap_pct > 0 ? "+" : ""}${r.gap_pct.toFixed(1)}%`}
              </div>
              <div className="col-span-2 text-xs text-right tabular-nums" style={{ color: gapColor(r.gap_pct) }}>
                {r.gap_total_usd == null ? "—" : `${r.gap_total_usd >= 0 ? "+" : "−"}${fmtUSD(Math.abs(r.gap_total_usd))}`}
              </div>
              <div className="col-span-2 flex flex-col items-start gap-1">
                <Pill spec={METHOD_PILL[r.compute_method]} />
                <Pill spec={ACTION_PILL[r.recommended_action]} />
                {r.stale_flag && (
                  <span className="text-[9px]" style={{ color: "#B45309" }}>● stale</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <DecisionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        decisionId={selected}
      />
    </div>
  );
}

function gapColor(pct) {
  if (pct == null) return "#64748B";
  if (Math.abs(pct) > 15) return "#9F1239";
  if (Math.abs(pct) > 5) return "#B45309";
  return SLATE_INK;
}

function Kpi({ label, value, sub, tone, testId }) {
  const color = tone === "tiffany" ? TIFFANY : tone === "amber" ? "#B45309" : SLATE_INK;
  return (
    <div className="p-3 rounded-md" style={{ background: "#FFFFFF", border: "1px solid #E2E8F0" }} data-testid={testId}>
      <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#64748B" }}>{label}</div>
      <div className="text-2xl mt-1 font-medium tabular-nums" style={{ color, fontFamily: '"Cormorant Garamond", serif' }}>{value}</div>
      {sub && <div className="text-[10px] mt-1" style={{ color: "#64748B" }}>{sub}</div>}
    </div>
  );
}

function Pill({ spec }) {
  if (!spec) return null;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider"
      style={{ background: spec.bg, color: spec.fg }}>
      {spec.label}
    </span>
  );
}
